import {
  PagedResponseEmpty,
  ValidationError,
} from "@ofqual/portal-core/core/types";
import { combineReducers, AnyAction } from "redux";
import { Attachment } from "../domain/Attachment";
import {
  INPUTTING_ITEM,
  CREATED_ITEM,
  CREATING_ITEM,
  CREATING_ITEM_VALIDATION_FAIL,
  LOADED_ITEM,
  LOADED_ITEMS,
  LOADING_ITEM,
  LOADING_ITEMS,
  SAVED_ITEM,
  UPDATING_ITEM,
  DELETE_ATTACHMENT,
  REOPEN_SAR,
  ASSIGNING_CASE,
  REJECT_SAR,
  LOADED_SHARE,
  PAUSE_SAR,
  UNPAUSE_SAR,
  LOADING_FOI,
  LOADED_FOI,
  CREATING_FOI,
} from "./actions";
import {
  CreateItemState,
  ItemsState,
  ItemState,
  TransferItemState,
} from "./state";
import { failedCrudResult, noCrudResult, successCrudResult } from "./types";

const itemsInitialState: ItemsState = {
  status: "NOT_LOADED",
  response: PagedResponseEmpty,
  hasData: false,
};

const items = (
  state: ItemsState = itemsInitialState,
  action: AnyAction
): ItemsState => {
  const { type, payload } = action;
  if (type === LOADING_ITEMS) {
    return {
      ...state,
      status: "LOADING",
    };
  }
  if (type === LOADED_ITEMS) {
    return {
      ...state,
      status: "LOADED",
      response: payload,
      hasData: true,
    };
  }
  return state;
};

const itemInitialState: ItemState = {
  status: "NOT_LOADED",
  item: null,
  lastCrudResult: noCrudResult(),
  attachmentsToDelete: [],
  share: null,
};

const item = (
  state: ItemState = itemInitialState,
  action: AnyAction
): ItemState => {
  const { type, payload } = action;
  switch (type) {
    case LOADING_ITEM:
      return {
        ...state,
        item: null,
        status: "LOADING",
        lastCrudResult: noCrudResult(),
        attachmentsToDelete: [],
      };
    case UPDATING_ITEM:
    case ASSIGNING_CASE:
    case REOPEN_SAR:
    case REJECT_SAR:
    case PAUSE_SAR:
    case UNPAUSE_SAR:
      return {
        ...state,
        status: "UPDATING",
        lastCrudResult: noCrudResult(),
      };
    case LOADED_ITEM:
      return {
        ...state,
        status: "LOADED",
        item: payload.item,
        share: payload.share ?? null,
      };
    case SAVED_ITEM:
      return {
        ...state,
        status: "LOADED",
        item: payload,
        lastCrudResult: successCrudResult(),
      };
    case DELETE_ATTACHMENT:
      const { attachment } = payload as {
        attachment: Attachment;
      };
      return {
        ...state,
        item: {
          ...state.item!,
          sarsAttachments: state.item!.sarsAttachments.filter(
            (x) => x.id !== attachment.id
          ),
        },
        attachmentsToDelete: [...state.attachmentsToDelete, attachment],
      };
    case LOADED_SHARE:
      return {
        ...state,
        share: payload.share,
      };
    default:
      return state;
  }
};

const createItemInitialState: CreateItemState = {
  status: "INPUTTING",
  lastCrudResult: noCrudResult(),
};
const createItem = (
  state: CreateItemState = createItemInitialState,
  action: AnyAction
): CreateItemState => {
  const { type, payload } = action;
  if (type === CREATING_ITEM) {
    return {
      ...state,
      status: "CREATING",
    };
  }
  if (type === CREATING_ITEM_VALIDATION_FAIL) {
    return {
      ...state,
      lastCrudResult: failedCrudResult(payload as ValidationError),
      status: "INPUTTING",
    };
  }
  if (type === CREATED_ITEM) {
    return {
      ...state,
      lastCrudResult: noCrudResult(),
      status: "CREATED",
    };
  }
  if (type === INPUTTING_ITEM) {
    return {
      ...state,
      lastCrudResult: noCrudResult(),
      status: "INPUTTING",
    };
  }
  return state;
};

const transferItemInitialState: TransferItemState = {
  status: "NOT_LOADED",
  item: null,
  lastCrudResult: noCrudResult(),
};

const transferRecord = (
  state: TransferItemState = transferItemInitialState,
  action: AnyAction
): TransferItemState => {
  const { type, payload } = action;

  switch (type) {
    case LOADING_FOI:
      return {
        ...state,
        item: null,
        status: "LOADING",
        lastCrudResult: noCrudResult(),
      };
    case LOADED_FOI:
      return {
        ...state,
        item: payload,
        status: "LOADED",
        lastCrudResult: successCrudResult(),
      };
      case CREATING_FOI: 
      return{
        ...state,
        status: "LOADING"
      }
    default:
      return state;
  }
};

export const rootReducer = combineReducers({
  items,
  item,
  createItem,
  transferRecord,
});
